import { BrowserStorage } from '@atc/browser-storage';
import isObject from 'isobject';

/**
 * @typedef {Object} cacheEvent
 * @property {String} action
 * @property {String} cacheType
 * @property {String} id
 */

export const rootKey = 'ATC_QUICK_SAVE_LISTINGS';

/**
 * Handles storing and caching personalization locally without authentication
 * @module LocalCache
 */
export default class LocalCache {
  constructor() {
    this.storage = new BrowserStorage(rootKey, {
      persist: true
    });
  }

  /**
   * @alias module:LocalCache
   * @return {this}
   */
  clearItems() {
    this.storage.removeItem();
    return this;
  }

  /**
   * @alias module:LocalCache
   * @return {Array}
   */
  getItems() {
    return this.storage.getItem() || [];
  }

  /**
   * @alias module:LocalCache
   * @return {cacheEvent}
   */
  addItem(item) {
    const items = this.getItems();

    // Allow inventory to be saved via id or object
    const id = isObject(item) ? item.id : item;
    if (!items.includes(id)) {
      items.push(id);
    }
    this.storage.setItem(items);
    return {
      action: 'add',
      cacheType: 'local',
      id
    };
  }

  /**
   * @alias module:LocalCache
   * @return {cacheEvent}
   */
  removeItem(item) {
    // Allow inventory to be saved via id or object
    const itemId = isObject(item) ? item.id : item;
    const items = this.getItems().filter(id => id !== itemId);
    this.storage.setItem(items);
    return {
      action: 'remove',
      cacheType: 'local',
      id: itemId
    };
  }

  /**
   * update the current cache with new items
   * @alias module:LocalCache
   * @param {Array} items list of ids
   * @return {Array} List of saved ids
   */
  addItems(items) {
    if (items === void 0) {
      items = [];
    }
    items.forEach(item => this.addItem(item));
    return this.getItems();
  }

  /**
   * Replace any current cached items with a new set
   * @alias module:LocalCache
   * @param {Array} items list of ids
   * @return {this}
   */
  setItems(items) {
    if (items === void 0) {
      items = [];
    }
    this.storage.setItem(items);
    return this;
  }
}